import React, { useEffect } from 'react';

const CustomTable = ({ 
  rows = [], 
  columns = [], 
  loading = false, 
  onEdit, 
  onDelete, 
  onView,
  pageSize = 10,
  page = 0,
  totalCount = 0,
  onPageChange,
  onPageSizeChange,
  sortModel = [],
  onSortModelChange,
  onRowClick,
  selectedRow
}) => {
  const totalPages = Math.ceil(totalCount / pageSize);

  // Close all dropdowns when clicking outside (mobile only)
  useEffect(() => {
    // Only add listener on mobile
    if (window.innerWidth >= 640) return; // sm breakpoint
    
    const handleClickOutside = (event) => {
      // Check if click is outside any dropdown or button
      const isDropdownClick = event.target.closest('[id^="mobile-menu-"]');
      const isButtonClick = event.target.closest('button[title="Actions"]');
      
      if (!isDropdownClick && !isButtonClick) {
        // Close all dropdowns
        rows.forEach(row => {
          const menu = document.getElementById(`mobile-menu-${row.id}`);
          if (menu) {
            menu.classList.add('hidden');
          }
        });
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, [rows]);

  const handleSort = (field) => {
    const currentSort = sortModel.find(sort => sort.field === field);
    let newSort = 'asc';
    
    if (currentSort) {
      newSort = currentSort.sort === 'asc' ? 'desc' : 'asc';
    }
    
    onSortModelChange([{ field, sort: newSort }]);
  };

  const getSortIcon = (field) => {
    const currentSort = sortModel.find(sort => sort.field === field);
    if (!currentSort) return '↕️';
    return currentSort.sort === 'asc' ? '↑' : '↓';
  };

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow-md border border-gray-200">
        <div className="p-6">
          <div className="animate-pulse space-y-4">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="h-4 bg-gray-200 rounded"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-md border border-gray-200 overflow-hidden">
      <style dangerouslySetInnerHTML={{
        __html: `
          @media (max-width: 639px) {
            .actions-column-mobile {
              width: 24px !important;
              min-width: 24px !important;
              max-width: 24px !important;
            }
            .actions-column-mobile::before {
              content: '';
              position: absolute;
              left: -1px;
              top: 0;
              height: 100%;
              width: 1px;
              background-color: #acb0b7;
              z-index: 20;
            }
            .table-header {
              border-left: none !important;
            }
          }
          @media (min-width: 640px) {
            .actions-column-desktop {
              border-left: 1px solid #6b7280 !important;
            }
            .table-header {
              border-left: none !important;
            }
          }
        `
      }} />
      {/* Table */}
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              {columns.map((column) => (
                <th
                  key={column.field}
                  className="table-header cursor-pointer hover:bg-gray-100"
                  onClick={() => column.sortable !== false && handleSort(column.field)}
                >
                  <div className="flex items-center justify-between">
                    <span>{column.headerName}</span>
                    {column.sortable !== false && (
                      <span className="ml-2 text-xs">{getSortIcon(column.field)}</span>
                    )}
                  </div>
                </th>
              ))}
              <th className="table-header sticky right-0 bg-gray-100 z-10 actions-column-mobile actions-column-desktop" style={{backgroundColor: '#f3f4f6'}}>
                <span className="hidden sm:inline">Actions</span>
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {rows.map((row, index) => (
              <tr 
                key={row.id || index} 
                className={`hover:bg-gray-50 cursor-pointer ${
                  selectedRow === row.id ? 'bg-blue-50 border-l-4 border-blue-500' : ''
                }`}
                onClick={() => onRowClick && onRowClick([row.id])}
              >
                {columns.map((column) => (
                  <td key={column.field} className="table-cell">
                    {column.valueGetter ? column.valueGetter({ value: row[column.field], row }) : row[column.field]}
                  </td>
                ))}
                <td className={`table-cell sticky right-0 z-10 relative actions-column-mobile actions-column-desktop ${
                  selectedRow === row.id ? 'bg-blue-50' : 'bg-gray-50'
                }`} style={{backgroundColor: selectedRow === row.id ? '#dbeafe' : '#f3f4f6'}}>
                  {/* Desktop: Show all buttons */}
                  <div className="hidden sm:flex space-x-2">
                    {onView && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          onView(row);
                        }}
                        className="p-2 text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded-md transition-colors"
                        title="View"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                        </svg>
                      </button>
                    )}
                    {onEdit && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          onEdit(row);
                        }}
                        className="p-2 text-green-600 hover:text-green-800 hover:bg-green-50 rounded-md transition-colors"
                        title="Edit"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                        </svg>
                      </button>
                    )}
                    {onDelete && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          onDelete(row);
                        }}
                        className="p-2 text-red-600 hover:text-red-800 hover:bg-red-50 rounded-md transition-colors"
                        title="Delete"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                      </button>
                    )}
                  </div>

                  {/* Mobile: Show 3 dots menu */}
                  <div className="sm:hidden flex justify-center items-center h-full">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        // Close all other open menus first
                        rows.forEach(otherRow => {
                          if (otherRow.id !== row.id) {
                            const otherMenu = document.getElementById(`mobile-menu-${otherRow.id}`);
                            if (otherMenu) {
                              otherMenu.classList.add('hidden');
                            }
                          }
                        });
                        
                        // Toggle mobile menu for this row
                        const menuId = `mobile-menu-${row.id}`;
                        const menu = document.getElementById(menuId);
                        if (menu) {
                          if (menu.classList.contains('hidden')) {
                            // Position the menu near the button
                            const rect = e.currentTarget.getBoundingClientRect();
                            menu.style.top = `${rect.bottom + 5}px`;
                            menu.style.right = `${window.innerWidth - rect.right}px`;
                            menu.classList.remove('hidden');
                          } else {
                            menu.classList.add('hidden');
                          }
                        }
                      }}
                      className="p-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-md transition-colors"
                      title="Actions"
                    >
                      <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M12 8c1.1 0 2-.9 2-2s-.9-2-2-2-2 .9-2 2 .9 2 2 2zm0 2c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2zm0 6c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2z"/>
                      </svg>
                    </button>
                    
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Mobile dropdown menus - rendered outside table to avoid z-index issues */}
      {rows.map((row) => (
        <div key={`mobile-menu-${row.id}`} id={`mobile-menu-${row.id}`} className="hidden fixed w-32 bg-white border border-gray-200 rounded-md shadow-lg z-[9999]">
          {onView && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onView(row);
                // Hide menu after action
                const menu = document.getElementById(`mobile-menu-${row.id}`);
                if (menu) menu.classList.add('hidden');
              }}
              className="w-full px-3 py-2 text-left text-blue-600 hover:bg-blue-50 flex items-center space-x-2"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
              </svg>
              <span>View</span>
            </button>
          )}
          {onEdit && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onEdit(row);
                // Hide menu after action
                const menu = document.getElementById(`mobile-menu-${row.id}`);
                if (menu) menu.classList.add('hidden');
              }}
              className="w-full px-3 py-2 text-left text-green-600 hover:bg-green-50 flex items-center space-x-2"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
              </svg>
              <span>Edit</span>
            </button>
          )}
          {onDelete && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onDelete(row);
                // Hide menu after action
                const menu = document.getElementById(`mobile-menu-${row.id}`);
                if (menu) menu.classList.add('hidden');
              }}
              className="w-full px-3 py-2 text-left text-red-600 hover:bg-red-50 flex items-center space-x-2"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
              <span>Delete</span>
            </button>
          )}
        </div>
      ))}

      {/* Pagination */}
      <div className="bg-white px-4 py-3 border-t border-gray-200 sm:px-6">
        {/* Mobile Layout */}
        <div className="sm:hidden">
          {/* Mobile: All controls in one line */}
          <div className="flex items-center justify-between">
            <button
              onClick={() => onPageChange(page - 1)}
              disabled={page === 0}
              className="relative inline-flex items-center p-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            
            <span className="text-sm text-gray-700 font-medium">
              Page {page + 1} of {totalPages}
            </span>
            
            <select
              value={pageSize}
              onChange={(e) => onPageSizeChange(parseInt(e.target.value))}
              className="border border-gray-300 rounded-md px-2 py-1 text-sm"
            >
              <option value={10}>10</option>
              <option value={20}>20</option>
            </select>
            
            <button
              onClick={() => onPageChange(page + 1)}
              disabled={page >= totalPages - 1}
              className="relative inline-flex items-center p-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
          </div>
        </div>
        
        {/* Desktop Layout */}
        <div className="hidden sm:flex sm:items-center sm:justify-between">
          <div>
            <p className="text-sm text-gray-700">
              Showing <span className="font-medium">{page * pageSize + 1}</span> to{' '}
              <span className="font-medium">
                {Math.min((page + 1) * pageSize, totalCount)}
              </span>{' '}
              of <span className="font-medium">{totalCount}</span> results
            </p>
          </div>
          <div className="flex items-center space-x-4">
            <div className="flex items-center">
              <label className="text-sm text-gray-700 mr-2">Rows per page:</label>
              <select
                value={pageSize}
                onChange={(e) => onPageSizeChange(parseInt(e.target.value))}
                className="border border-gray-300 rounded-md px-2 py-1 text-sm"
              >
                <option value={10}>10</option>
                <option value={20}>20</option>
                <option value={50}>50</option>
              </select>
            </div>
            <div className="flex space-x-1">
              <button
                onClick={() => onPageChange(page - 1)}
                disabled={page === 0}
                className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Previous
              </button>
              {[...Array(Math.min(5, totalPages))].map((_, i) => {
                const pageNum = Math.max(0, Math.min(totalPages - 5, page - 2)) + i;
                if (pageNum >= totalPages) return null;
                return (
                  <button
                    key={pageNum}
                    onClick={() => onPageChange(pageNum)}
                    className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                      pageNum === page
                        ? 'z-10 bg-blue-50 border-blue-500 text-blue-600'
                        : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                    }`}
                  >
                    {pageNum + 1}
                  </button>
                );
              })}
              <button
                onClick={() => onPageChange(page + 1)}
                disabled={page >= totalPages - 1}
                className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CustomTable;
